import { Product, WishlistDataContainer } from '../../@types/wishlist.js';
import { CookieHandler, CookieHandlerInstance } from './cookiehandler.js';
import {
  ADD_TO_WISHLIST_EVENT,
  ADDED_TO_WISHLIST_EVENT,
  config,
  LOGGED_OUT_EVENT,
  REMOVE_FROM_WISHLIST_EVENT,
  REMOVED_FROM_WISHLIST_EVENT,
} from './config.js';
import { WishlistApi, WishlistApiInstance } from './wishlist-api.js';
import { trackAddToWishlist, trackRemoveFromWishlist } from './cube-tracking-layer.js';
import { track } from './generic-tracking-layer.js';
import { SortAction } from '../filter/sort-action.js';
import { getLoginTransition, LAST_LOGIN_STATE_COOKIE, COOKIE_EXPIRATION_DAYS } from './login-state.js';

export class WishlistService {
  private readonly api: WishlistApi;

  private readonly cookieHandler: CookieHandler;

  declare private readonly onAddToWishlist: (_: CustomEvent<WishlistDataContainer>) => void;

  declare private readonly onRemoveFromWishlist: (_: CustomEvent<WishlistDataContainer>) => void;

  constructor(api: WishlistApi, cookieHandler: CookieHandler) {
    this.api = api;
    this.cookieHandler = cookieHandler;
    this.onAddToWishlist = (e: Event) => {
      this.addToWishlist((e as CustomEvent).detail);
    };
    this.onRemoveFromWishlist = (e) => {
      const data = (e as CustomEvent).detail;
      const trackingCallback = () => trackRemoveFromWishlist(data.syan, data.colorId, data.articleId, data.tracking);
      this.removeFromWishlist(data, trackingCallback);
    };
  }

  init() {
    this.destroy();
    document.addEventListener(ADD_TO_WISHLIST_EVENT, this.onAddToWishlist);
    document.addEventListener(REMOVE_FROM_WISHLIST_EVENT, this.onRemoveFromWishlist);
    this.handleCookiesForMergeDetection();
  }

  destroy() {
    document.removeEventListener(ADD_TO_WISHLIST_EVENT, this.onAddToWishlist);
    document.removeEventListener(REMOVE_FROM_WISHLIST_EVENT, this.onRemoveFromWishlist);
  }

  handleCookiesForMergeDetection() {
    const transition = getLoginTransition(this.cookieHandler);
    const userIsLoggedIn = config.currentlyLoggedIn === 'true';
    if (transition === 'login') {
      this.api.merge();
    }
    if (transition === 'logout') {
      document.dispatchEvent(new Event(LOGGED_OUT_EVENT));
    }
    this.cookieHandler.setCookie(LAST_LOGIN_STATE_COOKIE, userIsLoggedIn ? 'true' : 'false', COOKIE_EXPIRATION_DAYS);
  }

  static getProductsWithCollectionStatusSortedByDefaultSorting(
    products: Product[],
    wishlistCollectionId?: string,
  ): (Product & { isInCollection: boolean })[] {
    if (!wishlistCollectionId) {
      return products.map((p) => ({ ...p, isInCollection: false }));
    }
    return products.map((product) => {
      const isInCollection = product.collectionReferences.map((cr) => cr.collectionId).includes(wishlistCollectionId);
      return {
        ...product,
        isInCollection,
      };
    });
  }

  static getProductsForCollectionWithDefaultSorting(products: Product[], wishlistCollectionId: string): Product[] {
    return products
      .filter((p) => p.collectionReferences.map((r) => r.collectionId).includes(wishlistCollectionId))
      .map((p) => {
        const collectionReference = p.collectionReferences.find((r) => r.collectionId === wishlistCollectionId);
        return {
          ...p,
          cAdded: collectionReference?.added ?? new Date().toISOString(),
        };
      })
      .sort(SortAction.createRecentlyAddedSortStrategy().compareStrategy);
  }

  static sortSharedProductsByCollectionAdded(products: Product[], collectionId: string): Product[] {
    return products
      .map((p) => {
        const ref = p.collectionReferences.find((r) => r.collectionId === collectionId);
        return { ...p, cAdded: ref?.added ?? p.added };
      })
      .sort(SortAction.createRecentlyAddedSortStrategy().compareStrategy);
  }

  static trackGenericAddToWishlist() {
    track({ event: 'product.wishlistAdd' });
  }

  async addToWishlist(data: WishlistDataContainer) {
    // Prevent optimistic update if "added" or "notifyOnBackInStock" is set, as the item might already exist
    // on the wishlist and a failed request must not remove it
    const disableOptimisticUpdateWhenAddedOrNotifyIsSet =
      data.added !== undefined || data.notifyOnBackInStock !== undefined;
    if (!disableOptimisticUpdateWhenAddedOrNotifyIsSet) {
      // optimistic update
      WishlistService.dispatchAddedEvent(data);
    }
    const result = await this.api.add({
      syan: data.syan,
      colorId: data.colorId,
      articleId: data.articleId,
      added: data.added,
      purchased: data.purchased,
      notifyOnBackInStock: data.notifyOnBackInStock,
      collectionReferences: data.collectionReferences,
    });
    if (result) {
      if (disableOptimisticUpdateWhenAddedOrNotifyIsSet) {
        WishlistService.dispatchAddedEvent(data);
      }
      document.dispatchEvent(new Event('ents:mobileapp:wishlist:added')); // used by mobile team to update app whishlist counter
      trackAddToWishlist(data);
      WishlistService.trackGenericAddToWishlist();
    } else if (!disableOptimisticUpdateWhenAddedOrNotifyIsSet) {
      // roll back
      WishlistService.dispatchRemovedEvent(data);
    }
  }

  async changeItemOnWishlist(syan: number, colorId?: string, articleId?: string) {
    return this.api.add({
      syan,
      colorId,
      articleId,
    });
  }

  async removeFromWishlist(
    data: WishlistDataContainer,
    trackingCallback: () => void,
    onRollback?: () => void,
    abortSignal?: AbortSignal,
  ) {
    // optimistic update
    WishlistService.dispatchRemovedEvent(data);
    const result = await this.api.remove({
      syan: data.syan,
      colorId: data.colorId,
    });
    if (abortSignal?.aborted) return false;
    if (result) {
      document.dispatchEvent(new Event('ents:mobileapp:wishlist:removed')); // used by mobile team to update app whishlist counter
      trackingCallback();
    } else {
      // roll back
      if (onRollback) onRollback();
      WishlistService.dispatchAddedEvent(data);
      this.api.add({
        syan: data.syan,
        colorId: data.colorId,
        articleId: data.articleId,
        added: data.added,
        purchased: data.purchased,
        collectionReferences: data.collectionReferences,
      });
    }
    return result;
  }

  private static dispatchAddedEvent(data: WishlistDataContainer) {
    document.dispatchEvent(new CustomEvent(ADDED_TO_WISHLIST_EVENT, { detail: WishlistService.withItemId(data) }));
  }

  private static dispatchRemovedEvent(data: WishlistDataContainer) {
    document.dispatchEvent(new CustomEvent(REMOVED_FROM_WISHLIST_EVENT, { detail: WishlistService.withItemId(data) }));
  }

  // "itemId" is the official DIST term for articleId (ENTS-10800)
  private static withItemId(data: WishlistDataContainer): WishlistDataContainer {
    return { ...data, itemId: data.itemId ?? data.articleId };
  }
}

export const wishlistService = new WishlistService(WishlistApiInstance, CookieHandlerInstance);
